import { NotifyHelper } from 'classes/helpers/notify.helper';
import { HELP_URLS } from 'classes/helpers/url.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonTextInput } from 'components/common/form/text';
import { CommonLink } from 'components/common/link';
import { SettingRow } from 'components/common/settings-dialog/row';
import { MachineContext } from 'contexts/machine.context';
import { REMOTE_PRESET_OPTIONS, RemoteDevice } from 'enums/remote-controls';
import useLatest from 'hooks/useLatestRef';
import { t } from 'i18next';
import { IHotkeyConfig } from 'lib_ts/interfaces/i-remote-controls';
import { clone, isEqual } from 'lodash';
import { useCallback, useContext, useEffect, useState } from 'react';

const MAX_DELAY_SEC = 10;

interface IProps {
  manual: boolean;

  paused: boolean;

  lastKey: string;
  onChangeLastKey: (v: string) => void;
}

export const AccessoriesSectionAutoForm = (props: IProps) => {
  const {
    isActive,
    machine,
    update: updateMachine,
  } = useContext(MachineContext);

  const latestMachine = useLatest(machine);
  const latestUpdateMachine = useLatest(updateMachine);

  const [selectedPreset, setSelectedPreset] = useState<RemoteDevice>();

  const [hotKeys, setHotKeys] = useState(clone(machine.remote_hotkeys) ?? {});

  // change is reflected in UI immediately
  const mergeHotKeys = (config: Partial<IHotkeyConfig>) => {
    setHotKeys((prev) => ({ ...prev, ...config }));
  };

  // gather a bunch of edits (e.g. from typing) before submitting a single update
  useEffect(() => {
    if (isEqual(hotKeys, latestMachine.current.remote_hotkeys)) {
      return;
    }

    const timeout = setTimeout(() => {
      latestUpdateMachine.current({
        _id: latestMachine.current._id,
        remote_hotkeys: hotKeys,
      });
    }, 500);

    return () => clearTimeout(timeout);
  }, [hotKeys]);

  const handleKeydown = useCallback(
    (event: KeyboardEvent) => {
      if (!props.manual) {
        return;
      }

      if (props.paused) {
        return;
      }

      if (event.ctrlKey || event.shiftKey || event.altKey) {
        /** only listen for raw key presses without any modifiers */
        return;
      }

      event.stopPropagation();
      event.preventDefault();

      if (event.repeat) {
        return;
      }

      if (props.lastKey === event.code) {
        return;
      }

      props.onChangeLastKey(event.code);
    },
    [props.manual, props.paused, props.lastKey]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [handleKeydown]);

  const disabled = !isActive || (props.manual && !props.paused);

  const delay_s = (hotKeys?.delay_ms ?? 0) / 1000;

  return (
    <ErrorBoundary identifier="AccessoriesSectionAutoForm">
      <SettingRow
        header="settings.device-presets"
        description={
          <>
            <p>{t('settings.device-presets-msg')}</p>
            <CommonLink
              url={t('common.intercom-url') + HELP_URLS.SETTINGS_ACCESSORIES}
            >
              {t('common.read-more')}
            </CommonLink>
          </>
        }
        input={
          <CommonSelectInput
            id="remote-control-preset"
            name="selectedPreset"
            options={REMOTE_PRESET_OPTIONS.filter(
              (o) => o.value !== RemoteDevice.Empty
            )}
            value={selectedPreset}
            onChange={(v) => {
              setSelectedPreset((v as RemoteDevice) || RemoteDevice.Empty);

              const definition = REMOTE_PRESET_OPTIONS.find(
                (o) => o.value === v
              );

              if (!definition) {
                return;
              }

              mergeHotKeys(definition.mapping);
            }}
            disabled={disabled}
            optional
          />
        }
      />

      <SettingRow
        separatorBefore
        header="settings.action-send-pitch-fire-drop-ball"
        description={t('settings.action-send-pitch-fire-drop-ball-msg')}
        input={
          <CommonTextInput
            id="remote-control-fire"
            data-testid="fire-mapping"
            disabled={disabled}
            // 2-way bind to value so that preset menu changes will reflect right away
            value={hotKeys?.fire}
            placeholder="None"
            onChange={(v) => mergeHotKeys({ fire: v })}
          />
        }
      />

      <SettingRow
        separatorBefore
        header="settings.action-previous"
        description={t('settings.action-previous-msg')}
        input={
          <CommonTextInput
            id="remote-control-previous"
            data-testid="previous-mapping"
            disabled={disabled}
            // 2-way bind to value so that preset menu changes will reflect right away
            value={hotKeys?.previous}
            placeholder="None"
            onChange={(v) => mergeHotKeys({ previous: v })}
          />
        }
      />

      <SettingRow
        separatorBefore
        header="settings.action-next"
        description={t('settings.action-next-msg')}
        input={
          <CommonTextInput
            id="remote-control-next"
            data-testid="next-mapping"
            disabled={disabled}
            // 2-way bind to value so that preset menu changes will reflect right away
            value={hotKeys?.next}
            placeholder="None"
            onChange={(v) => mergeHotKeys({ next: v })}
          />
        }
      />

      <SettingRow
        separatorBefore
        header="settings.delay-seconds"
        description={t('settings.delay-seconds-msg', { s: delay_s })}
        input={
          <CommonTextInput
            id="remote-control-delay"
            type="number"
            disabled={disabled}
            value={delay_s.toString()}
            debounceMs={1_000}
            onNumericChange={(v) => {
              /** value is given in seconds, saved in ms */
              if (v < 0) {
                NotifyHelper.warning({
                  message_md: t('settings.input-non-negative-value-msg'),
                });
                return;
              }

              if (v > MAX_DELAY_SEC) {
                NotifyHelper.warning({
                  message_md: t('settings.input-value-less-than-seconds-msg', {
                    x: MAX_DELAY_SEC,
                  }),
                });
                return;
              }

              mergeHotKeys({
                delay_ms: v * 1_000,
              });
            }}
          />
        }
      />
    </ErrorBoundary>
  );
};
